<template>
  <div :class="$style.root">
    <BaseCarouselComponent
      @click="handleClick($event)"
      ref="sliderElement"
      :class="$style.carousel"
      v-if="images?.length > 1"
    >
      <BaseCarouselSlideComponent
        v-for="image in images"
        :key="image?.id"
      >
        <CoreImageComponent
          :image="image?.attributes"
        />
      </BaseCarouselSlideComponent>

      <template #bottom="{ slideCount, slideIndex, goToSlide }">
        <nav v-if="slideCount > 1" :class="$style.nav">
          <div
            :class="$style.buttonContainer"
            :style="`--offset: ${buttonOffset(slideCount, slideIndex - 1)}`"
          >
            <button
              v-for="buttonIndex in buttons(slideCount)"
              @click="goToSlide(buttonIndex)"
              :class="$style.button"
              :data-current="buttonIndex === (slideIndex - 1) ? '' : null"
              :data-within-range="buttonVisible(slideCount, (slideIndex - 1), buttonIndex) ? '' : null"
            />
          </div>

          <div>
            {{ slideIndex }}/{{ slideCount }}
          </div>
        </nav>
      </template>
    </BaseCarouselComponent>

    <CoreImageComponent
      v-else-if="images?.length === 1"
      :image="images[0]?.attributes"
      :class="$style.singleImage"
    />
  </div>
</template>

<script setup>
import { SwiperSlide } from 'swiper/vue'
import { range } from 'lodash-es'

const sliderElement = ref(null)

const props = defineProps({
  block: Object
})

const images = computed(() => {
  return i18nGetLocalizedData(props?.block)?.images?.data
})

const buttons = (count) => {
  return Array.from({ length: count }, (_, i) => i)
}

const visibleButtons = (slideCount, slideIndex) => {
  const buttonRange = 3
  if (slideIndex === 0) {
    return range(slideIndex + buttonRange)
  } else if ((slideIndex + buttonRange) < slideCount) {
    return range(slideIndex - 1, (slideIndex - 1) + buttonRange)
  } else if (slideIndex === (slideCount - buttonRange)) {
    return range((slideIndex - 1), (slideCount - 1))
  } else {
    return range(slideCount - buttonRange, slideCount)
  }
}

const buttonVisible = (slideCount, slideIndex, buttonIndex) => {
  return visibleButtons(slideCount, slideIndex).includes(buttonIndex)
}

const buttonOffset = (slideCount, slideIndex) => {
  const range = 3
  if (slideIndex === 0) {
    return 0
  } else if ((slideIndex + range) < slideCount) {
    return slideIndex - 1
  } else if (slideIndex === (slideCount - range)) {
    return slideCount - (range + 1)
  } else {
    return slideCount - range
  }
}

const handleClick = () => {
  if (sliderElement?.value?.slideIndex < sliderElement?.value?.slideCount) {
    sliderElement.value?.goToSlide(sliderElement?.value?.slideIndex )
  } else {
    sliderElement.value?.goToSlide(0)
  }
}

</script>

<style module lang=scss>
.root {
  max-width: 100vw;
}

.singleImage {
  composes: container--medium from global;

  --base--image--max-aspect-ratio: 3 / 2;

  @media (max-width: $breakpoint-s) {
    --base--image--max-aspect-ratio: 1;
  }
}

.carousel {
  composes: container--medium from global;

  display: block;
  overflow: hidden;
  --base--image--aspect-ratio: 3 / 2;

  @media (max-width: $breakpoint-s) {
    --base--image--aspect-ratio: 1;
  }
}

.nav {
  margin-top: var(--unit--vertical);

  display: grid;
  grid-template-columns: 1fr min-content;
  gap: var(--unit--spacer);
  align-items: baseline;
}

.buttonContainer {
  transform: translateX(calc((var(--offset) * (var(--stroke--thick) + var(--unit--spacer))) * -1));
  display: flex;
  gap: var(--unit--spacer);
  transition: transform .25s ease;
}

.button {
  composes: reset-button from global;
  width: var(--stroke--thick);
  height: var(--stroke--thick);
  border-radius: 9999px;
  background-color: var(--color--primary);

  transition: width .25s .25s ease, transform .2s ease;

  flex-shrink: 0;
}

.button:not([data-within-range]) {
  transform: scale(0);
  pointer-events: none;
}

.button[data-current] {
  width: 4ch;
}

.button:not([data-current]):hover {
  background-color: var(--color--accent);
}
</style>
